import { z } from 'zod';
import { TypedApiClient } from '../index';
import { schemas } from '../../generated/api-client';
import { apiPath } from '../path';

// Import from generated schemas
const {
  ProductFirestoreResponse,
  CreateProductDto,
  UpdateProductDto,
  BulkStockUpdateDto,
  BulkStockUpdateResponseDto,
  BulkCostCascadeDto,
  BulkCostCascadeResponseDto,
  SeedStoreProductSettingsDto,
  SeedStoreProductSettingsResponseDto,
} = schemas;

// Re-export schemas for external use
export const ProductResponseSchema = ProductFirestoreResponse;
export const CreateProductSchema = CreateProductDto;
export const UpdateProductSchema = UpdateProductDto;

// Type exports
export type ProductResponse = z.infer<typeof ProductResponseSchema>;
export type ProductWithStockResponse = ProductResponse; // Alias for backwards compat
export type CreateProduct = z.infer<typeof CreateProductSchema>;
export type UpdateProduct = z.infer<typeof UpdateProductSchema>;

// Query params types
interface GetProductsParams {
  storeId?: string;
  categoryId?: string;
  supplierId?: string;
  search?: string;
  [key: string]: string | undefined;
}

interface SearchProductsParams {
  q: string;
  limit?: number;
  [key: string]: string | number | undefined;
}

interface UpdateStockBody {
  storeId: string;
  newStock: number;
  reason?: string;
}

// Type exports for the bulk endpoints (ATL-765 / ATL-790)
export type BulkStockUpdate = z.infer<typeof BulkStockUpdateDto>;
export type BulkStockUpdateResponse = z.infer<typeof BulkStockUpdateResponseDto>;
export type BulkCostCascade = z.infer<typeof BulkCostCascadeDto>;
export type BulkCostCascadeResponse = z.infer<typeof BulkCostCascadeResponseDto>;
export type SeedStoreProductSettings = z.infer<typeof SeedStoreProductSettingsDto>;
export type SeedStoreProductSettingsResponse = z.infer<typeof SeedStoreProductSettingsResponseDto>;

// API factory function
export function createProductsApi(client: TypedApiClient) {
  return {
    getProducts: (params?: GetProductsParams): Promise<ProductResponse[]> => {
      return client.get('/products', {
        responseSchema: z.array(ProductFirestoreResponse),
        params: params as Record<string, unknown>,
      });
    },

    getProductById: (id: string): Promise<ProductResponse> => {
      return client.get(apiPath`/products/${id}`, {
        responseSchema: ProductFirestoreResponse,
      });
    },

    createProduct: (data: Partial<ProductResponse>): Promise<ProductResponse> => {
      return client.post('/products', {
        data,
        responseSchema: ProductFirestoreResponse,
      });
    },

    updateProduct: (id: string, data: Partial<ProductResponse>): Promise<ProductResponse> => {
      return client.put(apiPath`/products/${id}`, {
        data,
        responseSchema: ProductFirestoreResponse,
      });
    },

    deleteProduct: (id: string): Promise<{ message: string }> => {
      return client.delete(apiPath`/products/${id}`, {
        responseSchema: z.object({ message: z.string() }),
      });
    },

    getProductByBarcode: (barcode: string): Promise<ProductResponse> => {
      return client.get(apiPath`/products/barcode/${barcode}`, {
        responseSchema: ProductFirestoreResponse,
      });
    },

    searchProducts: (params: SearchProductsParams): Promise<ProductResponse[]> => {
      return client.get('/products/search', {
        responseSchema: z.array(ProductFirestoreResponse),
        params: params as Record<string, unknown>,
      });
    },

    updateStock: (
      id: string,
      data: UpdateStockBody,
      userId?: string,
    ): Promise<Record<string, unknown>> => {
      // ATL-843: the path segment is encoded by `apiPath`, and `userId` rides in `params` so
      // axios encodes the query value — no hand-assembled URL string on either side.
      return client.patch(apiPath`/products/${id}/stock`, {
        data,
        params: userId ? { userId } : undefined,
        responseSchema: z.record(z.unknown()),
      });
    },

    /**
     * ATL-765 — set absolute stock for many (product, store) pairs in one request. The response
     * is 200 with PER-ITEM results even when individual items fail; callers must read
     * `results[].ok`. `requestSchema` is deliberate: a non-numeric `newStock` must be rejected
     * client-side (Zod) rather than travel into the RPC cast (the single-item endpoint has no
     * such guard — see planStoreStockMirror's coercion comment).
     */
    updateStockBulk: (data: BulkStockUpdate): Promise<BulkStockUpdateResponse> => {
      return client.post('/products/stock/bulk', {
        data,
        requestSchema: BulkStockUpdateDto,
        responseSchema: BulkStockUpdateResponseDto,
      });
    },

    /**
     * ATL-950 — apply Firestore's recomputed composite costs to Supabase. `updateProduct()`
     * recomputes the purchasePrice of every composite containing the edited product into the
     * SAME Firestore batch and dual-writes only the edited product; this is the missing leg.
     * Per-item results, same contract as updateStockBulk — read `results[].ok`.
     */
    updateCostCascadeBulk: (data: BulkCostCascade): Promise<BulkCostCascadeResponse> => {
      return client.post('/products/cost-cascade/bulk', {
        data,
        requestSchema: BulkCostCascadeDto,
        responseSchema: BulkCostCascadeResponseDto,
      });
    },

    /**
     * ATL-790 — seed a NEW store's per-product copied sale price + visibility (the Supabase
     * counterpart of saveNewStore's Firestore product backfill). Per-item results, same contract
     * as updateStockBulk.
     */
    seedStoreProductSettings: (
      data: SeedStoreProductSettings,
    ): Promise<SeedStoreProductSettingsResponse> => {
      return client.post('/products/store-settings/seed', {
        data,
        requestSchema: SeedStoreProductSettingsDto,
        responseSchema: SeedStoreProductSettingsResponseDto,
      });
    },

    bulkResetStocks: (productIds: string[]): Promise<{ message: string }> => {
      return client.post('/products/bulk-reset-stocks', {
        data: { productIds },
        responseSchema: z.object({ message: z.string() }),
      });
    },

    getStockSnapshot: (id: string): Promise<Record<string, unknown>> => {
      return client.get(apiPath`/products/stock-snapshots/${id}`, {
        responseSchema: z.record(z.unknown()),
      });
    },
  };
}
